/* eslint-disable max-len */
/**
 * @summary This is the component that will set the Head of the site.
 *  This component will injected the needed SEO meta data and other global
 *  scripts needed for the site/page.
 */
import React from 'react';
import {
  GlobalContextType,
  PublicEnvironmentVariables,
  ServerSidePageProps,
} from 'types';
import get from 'lodash/get';
import Head from 'next/head';
import { initGtm } from 'utils/thirdPartyScripts';
import { getPageTitle } from 'utils/siteHeadHelpers';
import isDevelopment from 'utils/isDevelopment';
import KetchScript from './KetchScript';

interface SiteHeadProps {
  meta?: Partial<GlobalContextType['siteMeta']> & {
    ogImg?: string | undefined;
    url?: string;
  };
  availableThemes?: PublicEnvironmentVariables['availableThemes'];
  title?: string;
  configEnv?: PublicEnvironmentVariables['configEnv'];
  abTestingClientKey?: PublicEnvironmentVariables['abTestingClientKey'];
  taxonomyValues?: ServerSidePageProps['taxonomyValues'];
  activeExperiment?: ServerSidePageProps['activeExperiment'];
}

function SiteHead(props: SiteHeadProps): React.ReactElement {
  const {
    meta,
    availableThemes,
    title,
    configEnv,
    taxonomyValues,
    activeExperiment,
  } = props;

  const {
    domain,
    favicon,
    googleTagManagerSiteId,
    robots,
    ogImg,
    siteDescription,
    siteName,
    theme = 'default-theme',
    url,
    seoTitle,
    seoDescription,
    seoKeywords,
    schoolCode,
    canonicalDomain,
    siteType,
    isDraftMode = false,
    originSchoolCode = null,
    isABTestingEnabled,
    ketch,
  } = meta || {};

  const description = seoDescription || siteDescription;
  const pageTitle = getPageTitle(seoTitle, title, siteName);
  const canonicalUrl = `https://${canonicalDomain}${url}`;
  const robotsText =
    configEnv === 'production' && !domain?.includes('pre-prod') && !isDraftMode
      ? robots
      : 'noindex, nofollow';
  const isProductionAnalytics = configEnv === 'production';

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {favicon && (
        <>
          <link
            rel="icon"
            href={get(favicon, 'sizes.faviconBrowser.url')}
            sizes="32x32"
          />
          <link
            rel="apple-touch-icon"
            href={get(favicon, 'sizes.faviconIOS.url')}
            sizes="180x180"
          />
          <link
            rel="icon"
            href={get(favicon, 'sizes.faviconWindowsTile.url')}
            sizes="270x270"
          />
        </>
      )}
      <meta name="description" content={description} />
      <meta name="robots" content={robotsText} />
      <meta name="googlebot" content={robotsText} />
      <meta name="bingbot" content={robotsText} />
      <link rel="canonical" href={canonicalUrl} />
      {/* Open Graph meta data, used for social media cards */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`https://${domain}${url}`} />
      <meta property="og:site_name" content={siteName} />
      {ogImg && <meta property="og:image" content={ogImg} />}
      {seoKeywords && <meta name="keywords" content={seoKeywords} />}
      {/* <link rel="manifest" href="/manifest.json" /> */}

      <script
        dangerouslySetInnerHTML={{
          __html: `
          window.dataLayer = window.dataLayer || [];

          window.dataLayer.push({
            school_code: "${schoolCode}",
            isClickPortal: ${siteType === 'clickPortal'},
            origin_school_code: "${originSchoolCode}",
            taxonomy_parent_category: "${taxonomyValues?.parentCategories?.join(
              '|'
            )}",
            taxonomy_category: "${taxonomyValues?.categories?.join('|')}",
            taxonomy_degree: "${taxonomyValues?.degrees?.join('|')}",
            taxonomy_programs: "${taxonomyValues?.programs?.join('|')}"
          });

          if(${!isProductionAnalytics}) {
            window.dataLayer.push({debug_mode: 1, traffic_type: "internal"});
          }
          `,
        }}
      />

      <script
        dangerouslySetInnerHTML={{
          __html: `window.isABTestingEnabled = ${isABTestingEnabled}`,
        }}
      />

      {!isDevelopment() && (
        <script
          dangerouslySetInnerHTML={{
            __html: initGtm(googleTagManagerSiteId, { schoolCode }),
          }}
        />
      )}

      {/* yarn build:themes make these css files. They then get injected into .env which is passed to this component via the page */}
      {'<!-- Render the theme for this request -->'}
      {!isDevelopment() && (
        <>
          <link
            rel="preload"
            href={`/_next/static/css/${
              availableThemes?.[theme as keyof typeof availableThemes]
            }`}
            as="style"
          />
          <link
            rel="stylesheet"
            href={`/_next/static/css/${
              availableThemes?.[theme as keyof typeof availableThemes]
            }`}
          />
        </>
      )}
      <KetchScript isEnabled={ketch?.isEnabled} propertyId={schoolCode} />
      {/* A/B Testing Enabled From Wordpress */}
      {activeExperiment?.length && activeExperiment.length > 0 && (
        <script
          dangerouslySetInnerHTML={{
            __html: `window.tmsActiveExperiment = ${JSON.stringify(
              activeExperiment
            )};`,
          }}
        />
      )}
    </Head>
  );
}

export default SiteHead;
